/* eslint-disable no-unused-vars */
import React, { useCallback, useMemo } from 'react';
import moment from 'moment-timezone';
import { shallowEqual, useSelector } from 'react-redux';

import Select from '../../../components/form/Select';
import PERMISSION from '../../../constants/Permissions';
import { UNIT_ASSIGNMENT_MODALS } from '../../../constants/UnitAssignment';
import { SHIFT_STATUS } from '../../../constants/ShiftStatus';
import Utils from '../../../utils/utils';

import { user } from '../../../redux/user';

const ActionsDropdown = ({
	canGuarantee,
	getUnitName,
	hasAvailableUnits,
	hasUnitsEnabled,
	onCancel,
	onConfirm,
	onGuaranteedCheckboxChange,
	onGuaranteeBundleChange,
	permissions,
	setShowUnitAssignmentModal,
	setUnitAssignmentSlot,
	slot,
	isFlex = false,
	onAssign,
	onRouteSlot,
	onFacilityFloatSlot
}) => {
	const userData = useSelector(user, shallowEqual);

	const hasEditAssignmentPermission = useMemo(() => Utils.isAuthorizedExact(PERMISSION.EDIT_ASSIGNMENT, permissions), [
		permissions
	]);
	const hasFlexEditUnitAssignmentPermission = useMemo(
		() => Utils.isAuthorizedExact(PERMISSION.FLEX_ASSIGN_UNITS, permissions),
		[permissions]
	);

	const hasEditStatusPermission = useMemo(() => Utils.isAuthorizedExact(PERMISSION.EDIT, permissions), [permissions]);
	const hasFlexCancelPermission = useMemo(() => Utils.isAuthorizedExact(PERMISSION.FLEX_CANCEL, permissions), [
		permissions
	]);
	const hasCancelPermission = useMemo(() => Utils.isAuthorizedExact(PERMISSION.CANCEL, permissions), [permissions]);
	const hasToggleGuaranteePermission = useMemo(() => Utils.isAuthorizedExact(PERMISSION.GUARANTEE, permissions), [
		permissions
	]);

	const hasFlexCaregiverAssignmentPermission = useMemo(
		() => Utils.isAuthorizedExact(PERMISSION.FLEX_ADD_CAREGIVER, permissions),
		[permissions]
	);

	const hasRouteFlexPermission = useMemo(
		() => Utils.isAuthorizedExact(PERMISSION.PORTAL_EDIT_SHIFT_ROUTING, permissions),
		[permissions]
	);

	const editFlexShiftFacilitiesForManagementCompany = useMemo(
		() =>
			userData.facilitiesEnabled.filter(
				(facility) =>
					facility.management_company_id === slot.management_company_id &&
					!!facility.allow_flex_shifts &&
					userData.permissionsByFacility[facility.id].includes(PERMISSION.FLEX_ADD_EDIT_SHIFT)
			),
		[slot, userData.facilitiesEnabled, userData.permissionsByFacility]
	);

	const canGuaranteeSlot = !isFlex && canGuarantee && !slot.caregiver_id && !slot.is_cancelled;
	const slotHasBeenWorked = slot.caregiver_id && slot.outcome_id;
	const HCPHasCheckedIn = slot.outcome_code === SHIFT_STATUS.ARRIVED.toUpperCase();
	const HCPHasConfirmed = slot.slot_status?.toUpperCase() === SHIFT_STATUS.CONFIRMED.toUpperCase();
	const canConfirmSlot = !isFlex && !slot.is_cancelled && slot.can_confirm && !slotHasBeenWorked && !HCPHasConfirmed;
	const isReplacement = !!slot.replaced_caregiver_name;
	const hasContract = !!slot.contract_id;
	const canCancelOnDemandSlot =
		!isFlex &&
		!slot.is_cancelled &&
		(slot.caregiver_id || !HCPHasConfirmed || isReplacement || hasContract) &&
		(!slot.outcome_id || HCPHasCheckedIn);
	const canCancelFlexSlot = isFlex && !slot.outcome_id;
	const canAssignSlot = isFlex && moment.utc(slot.end_time_at_utc).isAfter(moment.utc()) && !slot.caregiver_id;
	const canEditAssignment = !isFlex && hasUnitsEnabled && hasAvailableUnits && hasEditAssignmentPermission;
	const canEditFlexAssignment = isFlex && hasUnitsEnabled && hasAvailableUnits && hasFlexEditUnitAssignmentPermission;

	const canRouteSlot =
		isFlex &&
		!slot.caregiver_id &&
		!!slot.enable_shift_routing &&
		moment.utc(slot.start_time_at_utc).isAfter(moment.utc());

	const canFacilityFloatSlot =
		!slot.is_orientation &&
		!!slot.caregiver_id &&
		userData.allowFlexFloating &&
		isFlex &&
		editFlexShiftFacilitiesForManagementCompany.length > 1;

	const getGuaranteeActionLabel = () => {
		if (hasContract) {
			return slot.is_guaranteed ? 'Remove Bundle Guarantee' : 'Guarantee Bundle';
		}
		return slot.is_guaranteed ? 'Remove Guarantee' : 'Guarantee Shift';
	};

	const getEditAssignmentLabel = () => {
		return slot.unit_id ? 'Update Unit' : 'Add Unit';
	};

	const getGuaranteeActionHandler = () => {
		if (hasContract) {
			return onGuaranteeBundleChange([slot]);
		}
		return onGuaranteedCheckboxChange([slot]);
	};

	const ACTION_ITEMS = useMemo(
		() => [
			{
				label: 'Confirm',
				value: 'CONFIRM',
				onSelect: () => onConfirm(slot),
				filter: () => hasEditStatusPermission && canConfirmSlot
			},
			{
				label: getEditAssignmentLabel(),
				value: 'EDIT_ASSIGNMENT',
				onSelect: () => {
					setUnitAssignmentSlot({ unit_name: getUnitName(slot.unit_id), ...slot });
					setShowUnitAssignmentModal(
						HCPHasCheckedIn ? UNIT_ASSIGNMENT_MODALS.editArrived : UNIT_ASSIGNMENT_MODALS.edit
					);
				},
				filter: () => canEditAssignment || canEditFlexAssignment
			},
			{
				label: 'Remove Unit',
				value: 'REMOVE_ASSIGNMENT',
				onSelect: () => {
					setUnitAssignmentSlot({ unit_name: getUnitName(slot.unit_id), ...slot });
					setShowUnitAssignmentModal(
						HCPHasCheckedIn ? UNIT_ASSIGNMENT_MODALS.unassignArrived : UNIT_ASSIGNMENT_MODALS.unassign
					);
				},
				filter: () => (canEditAssignment || canEditFlexAssignment) && slot.unit_id
			},
			{
				label: getGuaranteeActionLabel(),
				value: 'GUARANTEE_SLOT',
				onSelect: getGuaranteeActionHandler,
				filter: () => hasToggleGuaranteePermission && canGuaranteeSlot
			},
			{
				label: 'Cancel',
				value: 'CANCEL',
				onSelect: () => onCancel(slot),
				filter: () => (hasFlexCancelPermission && canCancelFlexSlot) || (hasCancelPermission && canCancelOnDemandSlot)
			},
			{
				label: 'Assign',
				value: 'ASSING_CAREGIVER',
				onSelect: () => {
					onAssign({ unit_name: getUnitName(slot.unit_id), ...slot });
				},
				filter: () => hasFlexCaregiverAssignmentPermission && canAssignSlot
			},
			{
				label: 'Modify Route',
				value: 'MODIFY_ROUTE',
				onSelect: () => onRouteSlot({ slot }),
				filter: () => hasRouteFlexPermission && canRouteSlot
			},
			{
				label: 'Facility Float',
				value: 'FACILITY_FLOAT',
				onSelect: () => onFacilityFloatSlot({ slot }),
				filter: () => canFacilityFloatSlot
			}
		],
		[
			slot,
			onConfirm,
			hasEditStatusPermission,
			canConfirmSlot,
			setUnitAssignmentSlot,
			getUnitName,
			setShowUnitAssignmentModal,
			HCPHasCheckedIn,
			hasEditAssignmentPermission,
			canEditAssignment,
			onGuaranteedCheckboxChange,
			hasToggleGuaranteePermission,
			canGuaranteeSlot,
			onCancel,
			hasFlexCancelPermission,
			canCancelFlexSlot,
			hasCancelPermission,
			canCancelOnDemandSlot,
			canAssignSlot,
			hasRouteFlexPermission
		]
	);

	const handleStatusChange = useCallback(
		({ updatedStatus: action }) => {
			const option = ACTION_ITEMS.find((_option) => _option.value === action);
			if (option) {
				option.onSelect();
			}
		},
		[ACTION_ITEMS]
	);

	const filteredOptions = ACTION_ITEMS.filter((option) => option.filter()).map((option) => ({
		label: option.label,
		value: option.value
	}));

	return (
		<div className="col text-sm-center">
			<Select
				className="select-status-daily-schedule"
				handleChange={handleStatusChange}
				isDisabled={!filteredOptions.length > 0}
				name="updatedStatus"
				options={filteredOptions}
				placeholder="Actions"
				value={slot.status}
				isSearchable={false}
			/>
		</div>
	);
};
export default ActionsDropdown;
